import MasterLayout from "../../../masterLayout/MasterLayout";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { createMRTColumnHelper } from "material-react-table";
import { baseUrl } from "../../../helper/urls";
import CustomTable from "../../../components/custom_table/CustomTable";
import { toast, ToastContainer } from "react-toastify";
import {
  IconButton,
  Stack,
  Tooltip,
  Modal,
  Box,
  Typography,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useSelector } from "react-redux";

const AdminCompletedBookingList = () => {
  const { token } = useSelector((state) => state.auth);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState("");

  const navigate = useNavigate();

  const url = `${baseUrl}/bookings/getBookingCompletedByAdmin`;

  const fetchBookings = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(url, { headers });
      setBookings(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  function formatUtcTimeTo12Hour(timeStr) {
  if (!timeStr) return "N/A";
  const d = new Date(timeStr);
  let h = d.getUTCHours(), m = d.getUTCMinutes(), s = d.getUTCSeconds();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")} ${ampm}`;
}
  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor("summary", {
      header: "View Summary",
      size: 30,
      Cell: ({ row }) => {
        const handleClick = () => {
          setSelectedSummary(row.original?.summary || "No summary available.");
          setShowSummaryModal(true);
        };

        return (
          <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            sx={{ textTransform: "none" }}
          >
            View Summary
          </Button>
        );
      },
    }),

    columnHelper.accessor("student_id", {
      header: "Student Name",
      size: 30,
      Cell: (cell) =>
        cell.row.original?.firstName + " " + cell.row.original?.lastName,
    }),

    columnHelper.accessor("counselor_id", {
      header: "Counselor Name",
      size: 30,
      Cell: (cell) =>
        cell.row.original?.c_firstname + " " + cell.row.original?.c_lastname,
    }),

    columnHelper.accessor("booking_date", {
      header: "Slot Date (UTC)",
      size: 30,
      // Cell: (cell) => cell.row.original.booking_date?.split("T")[0] || "N/A",
      Cell: (cell) => cell.row.original.utcTime?.split("T")[0] || "N/A",
    }),

    columnHelper.accessor("booking_time", {
      header: "Slot Time (UTC)",
      size: 30,
       Cell: (cell) => formatUtcTimeTo12Hour(cell.row.original.utcTime),
    }),

    columnHelper.accessor("reqId", {
      header: "Request ID",
      size: 20,
      Cell: (cell) => "DDR000" + cell.row.original?.reqId,
    }),
  ];

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 2,
  };

  const styles = {
    container: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      padding: "16px",
      background: "#fff",
      borderRadius: "8px",
      marginBottom: "20px",
      alignItems: "flex-end",
    },
    group: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },
    label: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#333",
    },
    input: {
      padding: "6px 10px",
      fontSize: "14px",
      height: "36px",
      border: "1px solid #ccc",
      borderRadius: "6px",
      minWidth: "180px",
    },
    iconButton: {
      width: "36px",
      height: "36px",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      transition: "0.2s ease-in-out",
    },
    primary: {
      backgroundColor: "#e3f2fd",
      color: "#1976d2",
    },
    primaryHover: {
      backgroundColor: "#bbdefb",
    },
    secondary: {
      backgroundColor: "#fce4ec",
      color: "#d32f2f",
    },
    secondaryHover: {
      backgroundColor: "#f8bbd0",
    },
  };

  return (
    <MasterLayout>
      <ToastContainer position="top-right" autoClose={2000} />
      <CustomTable columns={columns} data={bookings} loading={loading} />

      {/* Summary Modal */}
      <Modal
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        aria-labelledby="summary-modal-title"
        aria-describedby="summary-modal-description"
      >
        <Box sx={{ ...modalStyle, position: "relative" }}>
          {/* Cancel Button in Top Right Corner */}
          <button
            style={{
              position: "absolute", // ⬅ Required to place in corner
              top: "16px", // ⬅ Distance from top
              right: "16px",
              ...styles.iconButton,
              ...styles.secondary,
            }}
            onClick={() => setShowSummaryModal(false)}
            title="Clear Filter"
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.secondaryHover.backgroundColor;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor =
                styles.secondary.backgroundColor;
            }}
          >
            <Icon icon="material-symbols:cancel-outline-rounded" />
          </button>
          <Typography id="summary-modal-title" variant="h6" mb={2}>
            Summary
          </Typography>
          {/* Scrollable content box */}
          <Box
            id="summary-modal-description"
            sx={{
              maxHeight: "300px",
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
            <Typography>{selectedSummary}</Typography>
          </Box>
        </Box>
      </Modal>
    </MasterLayout>
  );
};

export default AdminCompletedBookingList;

// import MasterLayout from "../../../masterLayout/MasterLayout";
// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import { createMRTColumnHelper } from "material-react-table";
// import { baseUrl } from "../../../helper/urls";
// import CustomTable from "../../../components/custom_table/CustomTable";
// import { toast, ToastContainer } from "react-toastify";
// import { IconButton, Stack, Tooltip } from "@mui/material";
// import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// const AdminCompletedBookingList = () => {
//   const [bookings, setBookings] = useState([]);

//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [showSummaryModal, setShowSummaryModal] = useState(false);
// const [selectedSummary, setSelectedSummary] = useState("");
//   const navigate = useNavigate();

//   const url = `${baseUrl}/bookings/getBookingCompletedByAdmin`;
//   const { token } = useSelector((state) => state.auth);

//   const fetchBookings = async () => {
//     try {
//       const headers = {
//         "Content-Type": "application/json",
//         Accept: "application/json",
//         Authorization: `Bearer ${token}`,
//       };

//       const response = await axios.get(url, { headers });
//       //   console.log("response:::::::::::::::::::::", response.data.data);

//       setBookings(response.data.data || []);
//       setLoading(false);
//     } catch (error) {
//       setError(error);
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBookings();
//   }, []);

//   const columnHelper = createMRTColumnHelper();

//   const columns = [
//   columnHelper.accessor("summary", {
//   header: "View Summary",
//   size: 30,
//   Cell: ({ row }) => {
//     const handleClick = () => {
//       setSelectedSummary(row.original?.summary || "No summary available.");
//       setShowSummaryModal(true);
//     };

//     return (
//       <Button
//         variant="outlined"
//         size="small"
//         onClick={handleClick}
//         sx={{ textTransform: "none" }}
//       >
//         View Summary
//       </Button>
//     );
//   },
// });
//     columnHelper.accessor("student_id", {
//       header: "Student Name",
//       size: 30,
//       Cell: (cell) =>
//         cell.row.original?.firstName + " " + cell.row.original?.lastName,
//     }),
//     columnHelper.accessor("counselor_id", {
//       header: "Counselor Name",
//       size: 30,
//       Cell: (cell) =>
//         cell.row.original?.c_firstname + " " + cell.row.original?.c_lastname,
//     }),
//     columnHelper.accessor("booking_date", {
//       header: "Slot Date",
//       size: 30,
//       Cell: (cell) => cell.row.original.booking_date?.split("T")[0] || "N/A",
//     }),
//     columnHelper.accessor("booking_time", {
//       header: "Slot Time",
//       size: 30,
//     }),

//     columnHelper.accessor("reqId", {
//       header: "Request ID",
//       size: 20,
//       Cell: (cell) => "DDR000" + cell.row.original?.reqId,
//     }),
//   ];

//   return (
//     <MasterLayout>
//       <ToastContainer position="top-right" autoClose={2000} />
//       <CustomTable columns={columns} data={bookings} loading={loading} />
//     </MasterLayout>
//   );
// };

// export default AdminCompletedBookingList;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";

// import "datatables.net-dt/js/dataTables.dataTables.js";
// import "datatables.net-dt/css/dataTables.dataTables.css";
// import axios from "axios";
// import Swal from "sweetalert2";
// import { createMRTColumnHelper } from "material-react-table";

// // import { baseUrl } from "../../helper/urls.js";
// // import CustomTable from "../../components/custom_table/CustomTable.jsx";
// import { Chip } from "@mui/material";
// import MasterLayout from "../../../masterLayout/MasterLayout.jsx";
// import { baseUrl } from "../../../helper/urls.js";
// import CustomTable from "../../../components/custom_table/CustomTable.jsx";

// const AdminCompletedBookingList = () => {
//   const [bookings, setBookings] = useState([]);

//   // console.log("list:::::::::::::::::::::::", bookings);

//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const navigate = useNavigate();

//   const url = `${baseUrl}/bookings/getBookingCompletedByAdmin`;
//   const { token } = useSelector((state) => state.auth);

//   const fetchBookings = async () => {
//     try {
//       const headers = {
//         "Content-Type": "application/json",
//         Accept: "application/json",
//         Authorization: `Bearer ${token}`,
//       };

//       const response = await axios.get(url, { headers });
//       // console.log("response:::::::::::::::::::::", response.data);

//       setBookings(response.data.data || []);
//       setLoading(false);
//     } catch (error) {
//       setError(error);
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBookings();
//   }, []);

//   const handleDelete = (id) => {
//     Swal.fire({
//       html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
//       text: "This will permanently delete the booking.",
//       icon: "question",
//       showCancelButton: true,
//       confirmButtonColor: "#d33",
//       cancelButtonColor: "#3085d6",
//       confirmButtonText: "Yes, delete it!",
//       cancelButtonText: "Cancel",
//     }).then((result) => {
//       if (result.isConfirmed) {
//         axios
//           .delete(`${url}/${id}`, {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           })
//           .then(() => {
//             setBookings((prev) => prev.filter((item) => item.id !== id));
//             fetchBookings();
//             Swal.fire("Deleted!", "Booking has been deleted.", "success");
//           })
//           .catch((error) => {
//             console.error("Error deleting booking:", error);
//             const message =
//               error.response?.data?.message ||
//               error.message ||
//               "Failed to delete booking.";
//             Swal.fire("Error", message, "error");
//           });
//       }
//     });
//   };

//   const handleEdit = (booking) => {
//     navigate("/bookings", { state: { booking } });
//   };

//   const statusMap = {
//     0: "Pending",
//     1: "Accepted",
//     2: "Paid",
//     3: "Rejected",
//     4: "Upcoming",
//     5: "Completed",
//   };

//   const columnHelper = createMRTColumnHelper();

//   const columns = [
//     columnHelper.accessor("id", {
//       header: "ID",
//       size: 30,
//     }),
//     columnHelper.accessor("student_id", {
//       header: "Student Name",
//       size: 30,
//       Cell: (cell) =>
//         cell.row.original?.firstName + " " + cell.row.original?.lastName,
//     }),
//     columnHelper.accessor("counselor_id", {
//       header: "Counselor Name",
//       size: 30,
//       Cell: (cell) =>
//         cell.row.original?.c_firstname + " " + cell.row.original?.c_lastname,
//     }),
//     // columnHelper.accessor("slot_id", {
//     //   header: "Slot ID",
//     //   size: 30,
//     // }),
//     columnHelper.accessor("booking_date", {
//       header: "Booking Date",
//       size: 30,
//       Cell: (cell) => cell.row.original.booking_date?.split("T")[0] || "N/A",
//     }),
//     columnHelper.accessor("booking_time", {
//       header: "Booking Time",
//       size: 30,
//     }),
//     columnHelper.accessor("booking_duration", {
//       header: "Duration (min)",
//       size: 30,
//     }),
//     // columnHelper.accessor("status", {
//     //   header: "Status",
//     //   size: 30,
//     //   cell: ({ cell }) => {
//     //     const status = cell.row.original.status;
//     //     // console.log("data:::::::::::::;;", status);
//     //     return statusMap[status] || "Unknown";
//     //   },
//     // }),

//     columnHelper.accessor("status", {
//       header: "Status",
//       size: 30,
//       Cell: (cell) => {
//         const status = cell.row.original.status;
//         return statusMap[status] || "Unknown";
//       },
//     }),

//     // columnHelper.accessor("is_deleted", {
//     //   header: "Deleted",
//     //   size: 30,
//     //   Cell: (cell) => (cell.row.original?.status === 1 ? "Yes" : "No"),
//     // }),
//     // columnHelper.accessor("created_at", {
//     //   header: "Created At",
//     //   size: 30,
//     //   cell: ({ getValue }) => getValue()?.split("T")[0] || "N/A",
//     // }),
//     // columnHelper.accessor("updated_at", {
//     //   header: "Updated At",
//     //   size: 30,
//     //   cell: ({ getValue }) => getValue()?.split("T")[0] || "N/A",
//     // }),
//     // columnHelper.accessor('actions', {
//     //   header: 'Actions',
//     //   size: 30,
//     //   cell: ({ row }) => {
//     //     const rowData = row.original;

//     //     return (
//     //       <Box>
//     //         <button
//     //           onClick={() => handleEdit(rowData)}
//     //           className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
//     //         >
//     //           <Icon icon="lucide:edit" />
//     //         </button>

//     //         <button
//     //           onClick={() => handleDelete(rowData.id)}
//     //           className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
//     //           style={{ cursor: 'pointer' }}
//     //         >
//     //           <Icon icon="mingcute:delete-2-line" />
//     //         </button>
//     //       </Box>
//     //     );
//     //   },
//     // }),
//   ];

//   return (
//     <MasterLayout>
//       <CustomTable columns={columns} data={bookings} loading={loading} />;
//     </MasterLayout>
//   );
// };

// export default AdminCompletedBookingList;
